import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../src/components/KeyboardAwareView';
import { useAuthStore } from '../../src/stores/authStore';
import { useEncryptionStore } from '../../src/stores/encryptionStore';
import { encryptionApi } from '../../src/api/client';
import axios from 'axios';
import { deriveKey, hashRecoveryPhrase } from '../../src/utils/encryption';
import { cryptoKeyToRawBytes, storeSeed } from '../../src/utils/crypto';
import { isCryptoEnvironmentError } from '../../src/utils/cryptoErrors';
import { getUserFacingMessage } from '../../src/utils/errors';
import { deriveX25519KeyPair } from '../../src/utils/teamCrypto';
import { persistPersonalTeamKey, syncTeamKeys } from '../../src/utils/teamKeySync';
import { BIP39_WORDLIST } from '../../src/utils/bip39-wordlist';
import { PASTE_FEEDBACK_MS } from '../../src/constants';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

const bip39Set = new Set(BIP39_WORDLIST);

/**
 * Name the actual failure. This used to blame the recovery phrase for
 * everything that went wrong after the server had confirmed it — IndexedDB
 * refusing a write, WebCrypto missing on an http origin — which told people
 * their 24 words were wrong when they were not.
 */
function restoreFailureMessage(err: unknown): string {
  if (isCryptoEnvironmentError(err)) return err.message;
  if (axios.isAxiosError(err)) {
    return getUserFacingMessage(err, 'Could not reach the server. Please try again.');
  }
  return err instanceof Error && err.message
    ? err.message
    : 'Something went wrong. Please try again.';
}

const parseRecoveryPhrase = (text: string): string[] => {
  // Remove numbering like "1.", "2.", "10.", etc.
  const cleaned = text.replace(/\d+\.\s*/g, '');
  // Split on any whitespace, commas, or newlines
  const words = cleaned.split(/[\s,]+/).filter((w) => w.length > 0);
  return words.map((w) => w.trim().toLowerCase());
};

/**
 * Validate that all words are in the BIP39 wordlist.
 * Returns a list of invalid words with their 1-based positions.
 */
const findInvalidBip39Words = (wordList: string[]): { pos: number; word: string }[] => {
  const invalid: { pos: number; word: string }[] = [];
  for (let i = 0; i < wordList.length; i++) {
    if (wordList[i] && !bip39Set.has(wordList[i])) {
      invalid.push({ pos: i + 1, word: wordList[i] });
    }
  }
  return invalid;
};

export default function EncryptionRestoreScreen() {
  const router = useRouter();
  const [words, setWords] = useState<string[]>(Array(24).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteSuccess, setPasteSuccess] = useState(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  // Why this screen is showing, so the user is not simply handed a 24-word
  // form. `keyStorageError` means storage FAILED (the key may well still be
  // there). Otherwise, on the web, the likeliest story for a returning user is
  // that the browser cleared its stored data — Safari does so after seven days
  // without a visit — which nothing here can distinguish from a first visit,
  // hence "may".
  const keyStorageError = useEncryptionStore((s) => s.keyStorageError);
  const serverHasKey = useAuthStore((s) => s.user?.e2ee_setup_complete ?? false);
  const browserMayHaveForgottenKey = Platform.OS === 'web' && serverHasKey;

  const handleWordChange = (index: number, value: string) => {
    setWords((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleFillWords = () => {
    setError(null);
    setPasteSuccess(false);

    if (!pasteText.trim()) {
      setError('Please paste your recovery phrase first.');
      return;
    }

    const parsed = parseRecoveryPhrase(pasteText);

    if (parsed.length !== 24) {
      setError(`Expected 24 words but found ${parsed.length}. Please check your pasted text.`);
      return;
    }

    setWords(parsed);
    setPasteSuccess(true);
    setPasteText('');
    setTimeout(() => setPasteSuccess(false), PASTE_FEEDBACK_MS);
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    const trimmedWords = words.map((w) => w.trim().toLowerCase());
    const emptyCount = trimmedWords.filter((w) => !w).length;
    if (emptyCount > 0) {
      setError(`Please fill in all 24 words. ${emptyCount} words are missing.`);
      setIsSubmitting(false);
      return;
    }

    // Validate all words are valid BIP39 words before hitting the server
    const invalidWords = findInvalidBip39Words(trimmedWords);
    if (invalidWords.length > 0) {
      const positions = invalidWords.map((w) => `#${w.pos} ("${w.word}")`).join(', ');
      setError(`Invalid recovery words at position ${positions}. Please check for typos.`);
      setIsSubmitting(false);
      return;
    }

    try {
      const phraseString = trimmedWords.join(' ');
      const phraseHash = await hashRecoveryPhrase(phraseString);

      const { data } = await encryptionApi.verifyPhrase({
        recovery_phrase_hash: phraseHash,
      });

      if (!data.valid || !data.salt) {
        setError('Invalid recovery phrase. Please check your words and try again.');
        setIsSubmitting(false);
        return;
      }

      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('You are not signed in. Sign in and try again.');

      // The PBKDF2 output is the SEED — the X25519 identity that unwraps team
      // keys — not the device key. This used to `storeKey(key)` here, a holdover
      // from before teams, and left the device decrypting personal content with
      // the wrong key. The device key is the personal team key; it arrives via
      // the sync below and is persisted by `persistPersonalTeamKey`.
      const key = await deriveKey(phraseString, data.salt);
      const rawSeed = await cryptoKeyToRawBytes(key);
      await storeSeed(rawSeed, userId);

      const { privateKey: x25519PrivateKey } = deriveX25519KeyPair(rawSeed);
      await syncTeamKeys(x25519PrivateKey, data.team_keys ?? undefined);

      // Not optional, and not "non-fatal": without this the device has a seed
      // and no key, which is a device that cannot decrypt anything.
      const stored = await persistPersonalTeamKey(userId);
      if (!stored) {
        throw new Error(
          'Your recovery phrase is correct, but your account key could not be fetched from the server. Check your connection and try again.'
        );
      }

      useEncryptionStore.getState().setHasLocalKey(true);
      useEncryptionStore.getState().setE2EEComplete(true);
    } catch (err) {
      console.error('Recovery phrase restore failed:', err);
      setError(restoreFailureMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <Text style={styles.iconText}>{'\u{1F510}'}</Text>
          </View>
          <Text style={styles.title}>Set Up Encryption</Text>
          <Text style={styles.subtitle}>
            Enter your 24-word recovery phrase to restore your encryption key on this device.
          </Text>

          {keyStorageError ? (
            <View style={[styles.noticeBox, styles.noticeBoxWarning]} testID="restore.storage-error">
              <Text style={[styles.noticeText, styles.noticeTextWarning]}>
                This device could not read its stored key, so your recovery phrase may not be needed. {keyStorageError}
              </Text>
            </View>
          ) : browserMayHaveForgottenKey ? (
            <View style={styles.noticeBox} testID="restore.browser-cleared-notice">
              <Text style={styles.noticeText}>
                This browser has no key for your account. Browsers clear a site's stored data after a period without visits — Safari after seven days — so a key saved here earlier may be gone. Transfer it from a device that still has it, or enter your recovery phrase.
              </Text>
            </View>
          ) : null}

          <TouchableOpacity
            style={styles.qrOption}
            onPress={() => router.push('/(setup)/transfer-target')}
            activeOpacity={0.7}
            accessibilityLabel="Scan QR code to transfer key"
            accessibilityRole="button"
            testID="restore.scan-qr-button"
          >
            <Text style={styles.qrOptionIcon} accessible={false}>{'\u{1F4F7}'}</Text>
            <View style={styles.qrOptionTextContainer} accessible={false}>
              <Text style={styles.qrOptionTitle} accessible={false}>Scan QR Code</Text>
              <Text style={styles.qrOptionSubtitle} accessible={false}>
                Transfer key from another device instantly
              </Text>
            </View>
            <Text style={styles.qrOptionArrow} accessible={false}>{'\u203A'}</Text>
          </TouchableOpacity>

          <View style={styles.qrDivider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>OR USE RECOVERY PHRASE</Text>
            <View style={styles.dividerLine} />
          </View>

          <View style={styles.pasteArea}>
            <TextInput
              style={styles.pasteInput}
              value={pasteText}
              onChangeText={setPasteText}
              placeholder="Paste your 24-word recovery phrase here..."
              placeholderTextColor={colors.placeholder}
              multiline
              numberOfLines={4}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="off"
              textAlignVertical="top"
              accessibilityLabel="Paste your 24-word recovery phrase"
              testID="restore.paste-input"
            />
            <TouchableOpacity
              style={styles.fillButton}
              onPress={handleFillWords}
              activeOpacity={0.7}
              accessibilityLabel="Fill words from pasted phrase"
              accessibilityRole="button"
              testID="restore.fill-button"
            >
              <Text style={styles.fillButtonText} accessible={false}>Fill Words</Text>
            </TouchableOpacity>
            {pasteSuccess && (
              <Text style={styles.successText}>24 words filled!</Text>
            )}
          </View>

          <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>OR ENTER MANUALLY</Text>
            <View style={styles.dividerLine} />
          </View>

          <View style={styles.wordGrid}>
            {words.map((word, idx) => (
              <View key={idx} style={styles.wordInput}>
                <Text style={styles.wordNumber}>{idx + 1}.</Text>
                <TextInput
                  style={styles.input}
                  value={word}
                  onChangeText={(text) => handleWordChange(idx, text)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="off"
                  placeholder=""
                  accessibilityLabel={`Recovery word ${idx + 1}`}
                  testID={`restore.word-${idx + 1}`}
                />
              </View>
            ))}
          </View>

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.button, isSubmitting && styles.buttonDisabled]}
            onPress={handleSubmit}
            disabled={isSubmitting}
            activeOpacity={0.7}
            accessibilityLabel="Restore encryption key"
            accessibilityRole="button"
            accessibilityState={{ disabled: isSubmitting }}
            testID="restore.submit-button"
          >
            {isSubmitting ? (
              <View style={styles.loadingRow} accessible={false}>
                <ActivityIndicator size="small" color={colors.textOnPrimary} />
                <Text style={styles.buttonText} accessible={false}> Restoring Key...</Text>
              </View>
            ) : (
              <Text style={styles.buttonText} accessible={false}>Restore Key</Text>
            )}
          </TouchableOpacity>

          <Text style={styles.helpText}>
            Don't have your recovery phrase? Check the email sent when you first set up your account.
          </Text>
          <TouchableOpacity
            onPress={() => router.push('/(setup)/reset-warning')}
            activeOpacity={0.7}
            accessibilityLabel="Reset your account"
            accessibilityRole="button"
            testID="restore.reset-link"
          >
            <Text style={styles.resetLink}>
              Can't access your recovery phrase? Reset your account and start fresh.
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => useAuthStore.getState().logout()}
            style={styles.logoutButton}
            activeOpacity={0.7}
            accessibilityLabel="Log out"
            accessibilityRole="button"
            testID="restore.logout-link"
          >
            <Text style={styles.logoutText}>Log Out</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.brandedBackground },
  scrollContent: { flexGrow: 1 as const, justifyContent: 'center' as const, paddingHorizontal: 16, paddingVertical: 32 },
  card: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12, elevation: 5 },
  iconContainer: { width: 56, height: 56, borderRadius: 28, backgroundColor: theme.colors.primaryBg, justifyContent: 'center' as const, alignItems: 'center' as const, alignSelf: 'center' as const, marginBottom: 16 },
  iconText: { fontSize: 28 },
  title: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textPrimary, textAlign: 'center' as const, marginBottom: 8 },
  subtitle: { fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' as const, marginBottom: 16, lineHeight: 20 },
  pasteArea: { marginBottom: 16 },
  pasteInput: { borderWidth: 1, borderColor: theme.colors.borderMedium, borderStyle: 'dashed' as const, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: theme.colors.textPrimary, minHeight: 80, backgroundColor: theme.colors.bgSecondary },
  fillButton: { backgroundColor: theme.colors.success, borderRadius: 8, paddingVertical: 10, alignItems: 'center' as const, marginTop: 8 },
  fillButtonText: { color: theme.colors.textOnPrimary, fontSize: 14, fontWeight: '600' as const },
  successText: { color: theme.colors.success, fontSize: 13, fontWeight: '600' as const, textAlign: 'center' as const, marginTop: 6 },
  divider: { flexDirection: 'row' as const, alignItems: 'center' as const, marginBottom: 16 },
  dividerLine: { flex: 1 as const, height: 1, backgroundColor: theme.colors.border },
  dividerText: { fontSize: 11, color: theme.colors.textDisabled, fontWeight: '600' as const, marginHorizontal: 10 },
  wordGrid: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, justifyContent: 'space-between' as const, marginBottom: 16 },
  wordInput: { width: '48%' as const, flexDirection: 'row' as const, alignItems: 'center' as const, marginBottom: 8 },
  wordNumber: { fontSize: 12, color: theme.colors.textDisabled, width: 22, textAlign: 'right' as const, marginRight: 4 },
  input: { flex: 1 as const, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, fontSize: 14, color: theme.colors.textPrimary },
  errorBox: { backgroundColor: theme.colors.errorBg, borderWidth: 1, borderColor: theme.colors.errorBorder, borderRadius: 10, padding: 14, marginBottom: 12 },
  errorText: { color: theme.colors.errorText, fontSize: 14, lineHeight: 20 },
  button: { backgroundColor: theme.colors.primary, borderRadius: 10, paddingVertical: 14, alignItems: 'center' as const, justifyContent: 'center' as const, minHeight: 48 },
  buttonDisabled: { backgroundColor: theme.colors.borderMedium },
  loadingRow: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'center' as const },
  buttonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
  helpText: { fontSize: 12, color: theme.colors.textDisabled, textAlign: 'center' as const, marginTop: 16, lineHeight: 18 },
  resetLink: { fontSize: 12, color: theme.colors.error, textAlign: 'center' as const, marginTop: 12, lineHeight: 18, textDecorationLine: 'underline' as const },
  logoutButton: { alignItems: 'center' as const, marginTop: 16 },
  logoutText: { fontSize: 14, color: theme.colors.danger, fontWeight: '600' as const },
  qrOption: { flexDirection: 'row' as const, alignItems: 'center' as const, backgroundColor: theme.colors.primaryBg, borderWidth: 1, borderColor: theme.colors.primaryLight, borderRadius: 12, padding: 14, marginBottom: 16 },
  qrOptionIcon: { fontSize: 28, marginRight: 12 },
  qrOptionTextContainer: { flex: 1 as const },
  qrOptionTitle: { fontSize: 16, fontWeight: '700' as const, color: theme.colors.primaryDark, marginBottom: 2 },
  qrOptionSubtitle: { fontSize: 13, color: theme.colors.primary, lineHeight: 18 },
  qrOptionArrow: { fontSize: 24, color: theme.colors.primaryLight, fontWeight: '700' as const },
  qrDivider: { flexDirection: 'row' as const, alignItems: 'center' as const, marginBottom: 16 },
  noticeBox: { backgroundColor: theme.colors.bgSecondary, borderWidth: 1, borderColor: theme.colors.infoBorder, borderRadius: 10, padding: 12, marginBottom: 16 },
  noticeText: { color: theme.colors.infoText, fontSize: 13, lineHeight: 19 },
  noticeBoxWarning: { backgroundColor: theme.colors.warningBg, borderColor: theme.colors.warningBorder },
  noticeTextWarning: { color: theme.colors.warningText },
});
